import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, gql } from "@apollo/client";
import { toast } from "sonner";

import ActionsContainer from "@/components/Actions/ActionsContainer";
import { UploadDeliveryFormValues as SharedUploadDeliveryFormValues } from "@/components/Actions/Deliver/UploadDelivery";
import LinkMediaUser from "@/components/Actions/Deliver/LinkMediaUser";
import UserDeliveryConfirm from "./UserDeliveryConfirm";

import { useUserContext } from "@/contexts/UserContextProvider";
import { TASK_SHOTS_QUERY_PARAM } from "@/helpers/constants";
import { getArrayFilterFromParams } from "@/helpers/utils";

import { InjectedHttpUploadProps } from "@/components/common/HttpUpload/HttpUpload";

import {
  DeliveryTaskShotInput,
  DeliverySourceEnum,
  DeliverUserDeliveryMutation,
  DeliverUserDeliveryMutationVariables,
  UserTaskShotsQueryQuery,
  UserTaskShotsQueryQueryVariables,
  UserTaskShot,
  ListUserDeliveryFilesMutation,
  ListUserDeliveryFilesMutationVariables,
} from "@/__generated__/graphql";
import { USER_ASSIGNED_TASK_SHOTS_QUERY } from "@/graphql/queries/common/UserAssignedTaskShotsQuery";
import UserUploadDelivery from "./UserDelivery";

const DELIVER_USER_DELIVERY = gql`
  mutation DeliverUserDelivery(
    $folderPath: String!
    $deliveryPath: String
    $vendorProjectId: SmartID!
    $deliveryKeysToUserTaskShotIds: [DeliveryTaskShotInput]!
    $notes: String
    $attachments: [SmartID]
  ) {
    deliverUserDelivery(
      folderPath: $folderPath
      deliveryPath: $deliveryPath
      vendorProjectId: $vendorProjectId
      deliveryKeysToUserTaskShotIds: $deliveryKeysToUserTaskShotIds
      notes: $notes
      attachments: $attachments
    ) {
      success
      message
    }
  }
`;

const LIST_USER_DELIVERY_FILES = gql`
  mutation ListUserDeliveryFiles(
    $folderPath: String!
    $vendorProjectId: SmartID!
  ) {
    listUserDeliveryFiles(
      folderPath: $folderPath
      vendorProjectId: $vendorProjectId
    ) {
      success
      message
      userDeliveryFiles
    }
  }
`;

enum Steps {
  "Upload Delivery",
  "Link Media",
  "Confirmation",
}

type UploadDeliveryFormValues = SharedUploadDeliveryFormValues;

interface UserDeliverTaskProps {
  uploadProps: InjectedHttpUploadProps;
}

const UserDeliverTask: React.FC<UserDeliverTaskProps> = ({ uploadProps }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentProject, currentVendorProject } = useUserContext();

  const taskShotIds = getArrayFilterFromParams(
    searchParams.get(TASK_SHOTS_QUERY_PARAM),
  );
  const projectId =
    searchParams.get("project_id") ||
    currentVendorProject?.id ||
    currentProject?.id;

  const [uploadValues, setUploadValues] =
    useState<UploadDeliveryFormValues | null>(null);
  const [linkedMedia, setLinkedMedia] = useState<DeliveryTaskShotInput[]>([]);
  const [fetchedDeliveryFiles, setFetchedDeliveryFiles] = useState<string[]>(
    [],
  );

  const { data, loading, error } = useQuery<
    UserTaskShotsQueryQuery,
    UserTaskShotsQueryQueryVariables
  >(USER_ASSIGNED_TASK_SHOTS_QUERY, {
    variables: {
      projectId: projectId as string,
      userTaskShotIds: taskShotIds.length ? taskShotIds : undefined,
    },
    skip: !projectId,
    fetchPolicy: "network-only",
  });

  const taskShots =
    data?.userTaskShots?.edges?.map((e) => e.node as UserTaskShot) || [];

  const [deliverUserDelivery] = useMutation<
    DeliverUserDeliveryMutation,
    DeliverUserDeliveryMutationVariables
  >(DELIVER_USER_DELIVERY);

  const [listUserDeliveryFiles] = useMutation<
    ListUserDeliveryFilesMutation,
    ListUserDeliveryFilesMutationVariables
  >(LIST_USER_DELIVERY_FILES);

  const handleUploadNext = async (
    values: UploadDeliveryFormValues,
  ): Promise<boolean> => {
    if (values.source === DeliverySourceEnum.Http) {
      setUploadValues(values);
      return true;
    }

    try {
      const { data } = await listUserDeliveryFiles({
        variables: {
          folderPath: values.folderPath,
          vendorProjectId: projectId as string,
        },
      });

      const response = data?.listUserDeliveryFiles;
      if (!response) {
        toast.error("No response from server");
        return false;
      }
      if (!response.success) {
        toast.error(response.message || "Failed to fetch delivery files");
        return false;
      }
      if (!response.userDeliveryFiles?.length) {
        toast.error("No files found in folder");
        return false;
      }

      setFetchedDeliveryFiles(response.userDeliveryFiles || []);
      setUploadValues(values);

      return true;
    } catch (err) {
      toast.error(err instanceof Error ? `AAA: ${err.message}` : String(err));
      return false;
    }
  };

  const handleFinalSubmit = async (
    deliveryPath: string,
    notes?: string,
    attachments?: string[],
  ) => {
    if (!uploadValues || !projectId) return;

    try {
      const result = await deliverUserDelivery({
        variables: {
          folderPath: uploadValues.folderPath || "",
          deliveryPath,
          vendorProjectId: currentVendorProject?.id as string,
          deliveryKeysToUserTaskShotIds: linkedMedia,
          notes,
          attachments,
        },
      });

      if (result.data?.deliverUserDelivery?.success) {
        toast.success("Delivery submitted successfully!");
        uploadProps.taskCompleted?.();
        navigate(`/projects/${currentVendorProject?.id}/assigned`);
      } else {
        toast.error(
          result.data?.deliverUserDelivery?.message || "Delivery failed",
        );
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(message || "An error occurred");
    }
  };

  if (!projectId)
    return <div className="p-8 text-center">Loading project...</div>;
  if (loading) return <div className="p-8 text-center">Loading tasks...</div>;
  if (error)
    return (
      <div className="p-8 text-center text-red-500">
        Error loading tasks: {error.message}
      </div>
    );
  if (!taskShots.length)
    return (
      <div className="p-8 text-center text-red-500">No tasks to deliver</div>
    );

  return (
    <ActionsContainer title="Deliver Tasks" steps={Object.keys(Steps)}>
      {(step, setStep) => (
        <>
          {step === Steps["Upload Delivery"] && (
            <UserUploadDelivery
              onNext={async (values) => {
                const ok = await handleUploadNext(values);
                if (ok) setStep(Steps["Link Media"]);
              }}
            />
          )}

          {step === Steps["Link Media"] && uploadValues && (
            <LinkMediaUser
              taskShots={taskShots}
              uploadDeliveryFormValues={uploadValues}
              existingLinks={linkedMedia}
              setSubRuleSelection={() => {}}
              setDeliveryKeysToTaskShotIds={setLinkedMedia}
              deliveredFiles={
                fetchedDeliveryFiles.length
                  ? fetchedDeliveryFiles
                  : uploadValues?.files?.map((f) => f.name) || []
              }
              onNext={(links) => {
                if (!links || !links.length) {
                  toast.error("Please link at least one media file.");
                  return;
                }
                setLinkedMedia(links);
                setStep(Steps["Confirmation"]);
              }}
            />
          )}

          {step === Steps["Confirmation"] && uploadValues && (
            <UserDeliveryConfirm
              folderPath={uploadValues.folderPath}
              projectId={projectId}
              httpFiles={uploadValues.files}
              deliveryKeysToTaskShotIds={linkedMedia}
              taskShots={taskShots}
              onSubmit={handleFinalSubmit}
              uploadProps={uploadProps}
            />
          )}
        </>
      )}
    </ActionsContainer>
  );
};

export default UserDeliverTask;
